import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import DeleteQuizModal from "../../partials/quizzes/DeleteQuizModal";
import EditThemeModal from "../../partials/quizzes/EditThemeModal";
import SelectUserModal from "../../partials/users/SelectUserModal";

import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import endpoints from "../../api/endpoints";

import PaginationClassic from "../../components/PaginationClassic";
import Sidebar from "../../partials/Sidebar";
import Header from "../../partials/Header";
import SearchForm from "../../partials/actions/SearchForm";
import QuizTable from "../../partials/quizzes/admin/QuizTable";
import BottomAppBar from "../../partials/ui/BottomAppBar";

function AllQuizzes() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const { t } = useTranslation();

  const [selectedItems, setSelectedItems] = useState([]);

  const handleSelectedItems = (selectedItems) => {
    setSelectedItems([...selectedItems]);
    console.log([...selectedItems]);
  };

  const axiosPrivate = useAxiosPrivate();
  const controller = new AbortController();

  // Get User Profile
  const { auth } = useAuth();
  const user = auth?.user;

  // Get Stories
  const [quizzes, setQuizzes] = useState(Array(20).fill(""));
  const [targetQuiz, setTargetQuiz] = useState();
  const [searchString, setSearchString] = useState("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 200, {
    leading: true,
  });
  const [page, onChange] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editThemeModalOpen, setEditThemeModalOpen] = useState(false);
  const [selectUserModalOpen, setSelectUserModalOpen] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  // Render Stories
  const getQuiz = async () => {
    try {
      setIsFetching(true);
      // Check if debouncedSearchString
      let params = {};
      if (debouncedSearchString) {
        console.log("Search String: " + debouncedSearchString);
        params = { search: debouncedSearchString };
      }

      params = { ...params, offset, limit };

      const res = await axiosPrivate.get(`${endpoints.QUESTIONSETS_URL}/all`, {
        signal: controller.signal,
        params: params,
      });

      // Check update page number if total page is less than current page
      if (page > res?.data?.totalPages) {
        onChange(res?.data?.totalPages);
      }

      setQuizzes(res?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    getQuiz();
  }, []);

  useEffect(() => {
    // Render Stories
    getQuiz();
  }, [debouncedSearchString, offset, limit]);

  const setThemeHandler = (quiz) => {
    setTargetQuiz(quiz);
    setEditThemeModalOpen(true);
  };

  const setUserHandler = (quiz) => {
    setTargetQuiz(quiz);
    setSelectUserModalOpen(true);
  };

  const deleteQuizHandler = (quiz) => {
    setTargetQuiz(quiz);
    setDeleteModalOpen(true);
  };

  return (
    <div className="flex overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        user={user}
      />

      {/* Content area */}
      <div className="md:h-screen relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header
          title="All Quizzes"
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          user={user}
        />

        <main>
          <div className="px-4 sm:px-6 lg:px-8 py-20 w-full max-w-9xl mx-auto">
            <DeleteQuizModal
              quiz={targetQuiz}
              callback={getQuiz}
              open={deleteModalOpen}
              setOpen={setDeleteModalOpen}
              redirect={false}
            />

            <SelectUserModal
              quiz={targetQuiz}
              quizzes={quizzes}
              setQuizzes={setQuizzes}
              open={selectUserModalOpen}
              setOpen={setSelectUserModalOpen}
            />

            <EditThemeModal
              quiz={targetQuiz}
              quizzes={quizzes}
              setQuizzes={setQuizzes}
              open={editThemeModalOpen}
              setOpen={setEditThemeModalOpen}
            />

            {/* Page header */}
            <h1 className="text-2xl md:text-3xl text-black dark:text-white font-bold mb-8">
              {t("All Quizzes")} ✨
            </h1>
            <div className="sm:flex sm:justify-between sm:items-center sm:mb-8">
              {/* Left: Title */}
              <div className="mb-4 sm:mb-0 grid grid-flow-col sm:auto-cols-max justify-between sm:justify-start gap-2">
                {/* Search form */}
                <SearchForm value={searchString} setValue={setSearchString} />
                {/* Filter button */}
              </div>

              {/* Right: Actions */}
              <div className="grid grid-flow-col sm:auto-cols-max justify-between gap-2">
                {/* Pagination */}
                <PaginationClassic
                  offset={offset}
                  setOffset={setOffset}
                  limit={limit}
                  setLimit={setLimit}
                  result={quizzes.length}
                  isFetching={isFetching}
                />
              </div>
            </div>

            {/* Table */}
            <QuizTable
              quizzes={quizzes}
              selectedItems={selectedItems}
              handleSelectedItems={handleSelectedItems}
              isLoading={isFetching}
              setThemeHandler={setThemeHandler}
              setUserHandler={setUserHandler}
              deleteQuizHandler={deleteQuizHandler}
            />
          </div>

          {/* Bottom App Nav bar */}
          <BottomAppBar />
        </main>
      </div>
    </div>
  );
}

export default AllQuizzes;
